import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { cn } from '@/lib/utils';
import { LoadingIcon } from '../Svg/loader-icon';

const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-[3px] text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default:
          'bg-background-1 text-primary-foreground shadow hover:bg-[#0B005B]/80',
        primary:
          'bg-primary text-primary-foreground shadow hover:bg-primary/80 rounded-full',
        destructive:
          'bg-destructive text-destructive-foreground shadow-sm hover:bg-destructive/90',
        outline:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground',
        secondary:
          'bg-secondary text-secondary-foreground shadow-sm hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        special:
          'shadow  w-auto px-6 bg-primary hover:bg-background-1 text-background-1 hover:text-primary transition-colors',
        link: 'text-primary underline-offset-4 hover:underline',
        outlineDestructive:
          'border border-input bg-background shadow-sm hover:bg-accent hover:text-accent-foreground border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors rounded-sm',
        outlinePrimary:
          'border border-input bg-background shadow-sm  hover:text-accent-foreground  text-destructive hover:bg-destructive rounded-sm border-background-1 text-background-1 hover:bg-background-1 hover:text-primary transition-colors',
      },
      size: {
        default: 'h-9 px-4 py-2',
        sm: 'h-8 rounded-md px-3 text-xs',
        lg: 'h-10 rounded-md px-8',
        icon: 'h-9 w-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
  isLoading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      variant,
      size,
      asChild = false,
      isLoading,
      children,
      ...props
    },
    ref
  ) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        disabled={isLoading}
        {...props}
      >
        {isLoading && <LoadingIcon className="w-4 h-4 mx-2 animate-spin" />}
        {!isLoading && children}
      </Comp>
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
